import {
  AnxietyLevelId,
  ChallengeId,
  FictionInterestId,
  GoalId,
  MotivationId,
  NonFictionInterestId,
  PersonTypeId,
  ProcrastinationFrequencyId,
  ReaderGoalId,
} from './quiz';

export const allBookIds = [
  'randomWalkDownWallStreet',
  'theSimplePathToWealth',
  'theTotalMoneyMakeover',
  'theLeanStartup',
  'theMillionaireRealEstateInvestor',
  'the7HabitsOfHighlyEffectivePeople',
  'thePsychologyOfMoney',
  'theWarOfArt',
  'burnout',
  'radicalCandor',
  'richDadPoorDad',
  'drive',
  'yourMoneyOrYourLife',
  'zeroToOne',
  'the5LoveLanguages',
  'attached',
  'theWholeBrainChild',
  'vagabonding',
  'atlasObscura',
  'theBodyKeepsTheScore',
  'mansSearchForMeaning',
  'atomicHabits',
  'theObesityCode',
  'thinkAndGrowRich',
  'essentialism',
  'grit',
  'daringGreatly',
  'sapiens',
  'quiet',
  'the8020Principle',
  'bible',
  'theCaseForChrist',
  'itEndsWithUs',
  'meBeforeYou',
  'goneGirl',
  'theGirlWithTheDragonTattoo',
  'theLordOfTheRings',
  'aGameOfThrones',
  'dune',
  'projectHailMary',
  'theBookThief',
  'theHelp',
  'it',
  'theExorcist',
  'whereTheCrawdadsSing',
  'theSevenHusbandsOfEvelynHugo',
  'aCourtOfThornsAndRoses',
  'fourthWing',
  // The en cover (assets/en/books/Onyx Storm.jpg) and audio sample
  // (assets/en/books/audio/Onyx Storm.mp3) are deployed; non-en locales still
  // only have manifest placeholders, so keep it out of interest maps and
  // funnels that ship localized assets until those are deployed too.
  'onyxStorm',
  // en-only so far — no localized manifest entries or assets yet, so these stay
  // out of the interest maps and are only reachable through the `book` param
  // (getPromotedBook falls back silently when a locale has no entry).
  'the48LawsOfPower',
  'fireAndBlood',
  'theFrozenRiver',
  'butcherAndBlackbird',
  'quran',
] as const;

export type BookId = (typeof allBookIds)[number];

// Books an ad creative can promote via the `book` query param
// (e.g. ?book=bible). A promoted book is guaranteed to be presented as the
// first book-sample step and is reported to the server together with the
// samples the user liked.
//
// This works in every funnel variant, not just this one: they all bootstrap
// funnel's AppComponent (which reads the param) and provide a FunnelFlowService
// subclass, and the promoted book is applied in `assignBooks` — private, so a
// subclass can only re-rank the pool it goes in front of, never bypass it.
//
// An id whose sample assets are missing in the visitor's
// locale degrades to the ranked books rather than breaking the funnel, so the
// en-only titles are safe to list here — but only point localized ads at ids
// whose assets are deployed for the locale.
export const promotedBookIds = [
  'bible',
  'onyxStorm',
  'mansSearchForMeaning',
  'thinkAndGrowRich',
  'itEndsWithUs',
  'the48LawsOfPower',
  'fireAndBlood',
  'theFrozenRiver',
  'butcherAndBlackbird',
  'quran',
] as const satisfies readonly BookId[];

export type PromotedBookId = (typeof promotedBookIds)[number];

// Ad params arrive in unpredictable casing (bible, Bible, onyxstorm), so
// match case-insensitively.
export function findPromotedBookId(param: string | null | undefined): PromotedBookId | undefined {
  const normalized = param?.trim().toLowerCase();

  if (!normalized) {
    return undefined;
  }

  return promotedBookIds.find(bookId => bookId.toLowerCase() === normalized);
}

export const goalsBookIdsMap = {
  beMoreProductive: ['the8020Principle', 'atomicHabits'],
  learnSomethingNew: ['thinkAndGrowRich', 'sapiens'],
  listenOnTheGo: ['atomicHabits', 'drive'],
  makeReadingEasier: ['essentialism', 'theBodyKeepsTheScore'],
  everythingIsPerfect: ['atomicHabits', 'the8020Principle'],
  adhdEyeStrainDyslexia: ['theBodyKeepsTheScore', 'theObesityCode'],
} as const satisfies Record<GoalId, readonly BookId[]>;

export const challengesBookIdMap = {
  noFreeTime: 'essentialism',
  stuckInRoutine: 'atomicHabits',
  constantStress: 'burnout',
  toxicEnvironment: 'radicalCandor',
  procrastination: 'theWarOfArt',
  lackOfMotivation: 'drive',
  lowSelfEsteem: 'mansSearchForMeaning',
  notSureWhereToStart: 'the7HabitsOfHighlyEffectivePeople',
  everythingIsPerfect: 'thinkAndGrowRich',
} as const satisfies Record<ChallengeId, BookId>;

export const motivationBookIdMap = {
  advanceCareer: 'the7HabitsOfHighlyEffectivePeople',
  excelEducation: 'atomicHabits',
  supportMentalHealth: 'theBodyKeepsTheScore',
  manageAdhd: 'atomicHabits',
  improveFocus: 'essentialism',
  overcomeDyslexia: 'essentialism',
  reduceEyeStrain: 'theBodyKeepsTheScore',
  saveTime: 'the8020Principle',
  enjoyEntertainment: 'vagabonding',
  personalGrowth: 'atomicHabits',
} as const satisfies Record<MotivationId, BookId>;

// Main funnel book1/book2 pool. Only ids with audio and cover in every funnel locale belong here -
// getBook throws on a missing manifest entry.
export const mainFunnelMotivationBookIdsMap = {
  advanceCareer: [
    'the7HabitsOfHighlyEffectivePeople',
    'thePsychologyOfMoney',
    'richDadPoorDad',
    'theLeanStartup',
    'radicalCandor',
    'zeroToOne',
  ],
  excelEducation: ['atomicHabits', 'sapiens', 'grit'],
  supportMentalHealth: ['theBodyKeepsTheScore', 'mansSearchForMeaning', 'daringGreatly', 'burnout'],
  manageAdhd: ['atomicHabits', 'essentialism', 'the8020Principle', 'drive'],
  improveFocus: ['essentialism', 'the8020Principle', 'atomicHabits', 'drive'],
  overcomeDyslexia: ['essentialism', 'grit'],
  reduceEyeStrain: ['theBodyKeepsTheScore', 'mansSearchForMeaning'],
  saveTime: ['the8020Principle', 'essentialism', 'atomicHabits'],
  enjoyEntertainment: [
    'itEndsWithUs',
    'whereTheCrawdadsSing',
    'theSevenHusbandsOfEvelynHugo',
    'projectHailMary',
    'goneGirl',
    'atlasObscura',
  ],
  personalGrowth: [
    'atomicHabits',
    'mansSearchForMeaning',
    'daringGreatly',
    'grit',
    'thinkAndGrowRich',
  ],
} as const satisfies Record<MotivationId, readonly BookId[]>;

export const mainFunnelGoalBookIdsMap = {
  beMoreProductive: ['the8020Principle', 'atomicHabits', 'essentialism', 'drive'],
  learnSomethingNew: ['thinkAndGrowRich', 'sapiens', 'atlasObscura', 'zeroToOne'],
  listenOnTheGo: ['atomicHabits', 'drive', 'projectHailMary', 'whereTheCrawdadsSing'],
  makeReadingEasier: ['essentialism', 'theBodyKeepsTheScore', 'theSevenHusbandsOfEvelynHugo'],
  everythingIsPerfect: [
    'atomicHabits',
    'the8020Principle',
    'theSevenHusbandsOfEvelynHugo',
    'projectHailMary',
  ],
  adhdEyeStrainDyslexia: ['theBodyKeepsTheScore', 'mansSearchForMeaning', 'essentialism'],
} as const satisfies Record<GoalId, readonly BookId[]>;

// Only the answers that admit to the struggle map to a book; the milder ones carry no signal.
export const procrastinationBookIdsMap = {
  always: [challengesBookIdMap.procrastination, challengesBookIdMap.lackOfMotivation],
  frequently: [challengesBookIdMap.procrastination, challengesBookIdMap.lackOfMotivation],
} as const satisfies Partial<Record<ProcrastinationFrequencyId, readonly BookId[]>>;

export const anxietyBookIdsMap = {
  yesSignificant: [challengesBookIdMap.constantStress, 'theBodyKeepsTheScore'],
  yesSomeExtent: [challengesBookIdMap.constantStress, 'theBodyKeepsTheScore'],
} as const satisfies Partial<Record<AnxietyLevelId, readonly BookId[]>>;

export const personTypeBookIdsMap = {
  introvert: ['quiet'],
} as const satisfies Partial<Record<PersonTypeId, readonly BookId[]>>;

export const readerGoalBookIdsMap = {
  relaxation: [
    'theSevenHusbandsOfEvelynHugo',
    'whereTheCrawdadsSing',
    'meBeforeYou',
    'aCourtOfThornsAndRoses',
  ],
  personalGrowth: ['atomicHabits', 'mansSearchForMeaning', 'daringGreatly', 'theBodyKeepsTheScore'],
  learningSkills: [
    'sapiens',
    'the7HabitsOfHighlyEffectivePeople',
    'thePsychologyOfMoney',
    'theLeanStartup',
  ],
  schoolStudy: ['sapiens', 'atomicHabits', 'theBookThief', 'the7HabitsOfHighlyEffectivePeople'],
  work: [
    'radicalCandor',
    'theLeanStartup',
    'the7HabitsOfHighlyEffectivePeople',
    'thePsychologyOfMoney',
  ],
  languagePractice: ['theBookThief', 'theHelp', 'vagabonding', 'atlasObscura'],
} as const satisfies Record<ReaderGoalId, readonly BookId[]>;

export const nonFictionInterestBookIdsMap = {
  selfImprovement: [
    'atomicHabits',
    'the7HabitsOfHighlyEffectivePeople',
    'mansSearchForMeaning',
    'grit',
    'daringGreatly',
    'essentialism',
  ],
  productivity: [
    'atomicHabits',
    'the8020Principle',
    'essentialism',
    'the7HabitsOfHighlyEffectivePeople',
    'drive',
  ],
  businessCareers: [
    'richDadPoorDad',
    'thePsychologyOfMoney',
    'the7HabitsOfHighlyEffectivePeople',
    'theLeanStartup',
    'zeroToOne',
    'radicalCandor',
  ],
  relationshipsHealth: [
    'theBodyKeepsTheScore',
    'the5LoveLanguages',
    'attached',
    'burnout',
    'theWholeBrainChild',
    'daringGreatly',
    'theObesityCode',
  ],
  history: ['sapiens', 'atlasObscura', 'mansSearchForMeaning'],
  scienceTechnology: ['sapiens', 'zeroToOne', 'theLeanStartup', 'theObesityCode'],
  biographies: ['mansSearchForMeaning', 'thinkAndGrowRich', 'richDadPoorDad'],
  philosophySpirituality: [
    'mansSearchForMeaning',
    'theWarOfArt',
    'bible',
    'theCaseForChrist',
    'sapiens',
  ],
  other: ['atomicHabits', 'sapiens', 'vagabonding', 'atlasObscura', 'quiet'],
} as const satisfies Record<NonFictionInterestId, readonly BookId[]>;

export const fictionInterestBookIdsMap = {
  contemporaryRomance: ['itEndsWithUs', 'meBeforeYou'],
  mysteryThriller: ['goneGirl', 'theGirlWithTheDragonTattoo'],
  fantasy: ['theLordOfTheRings', 'aGameOfThrones'],
  scienceFiction: ['dune', 'projectHailMary'],
  historicalFiction: ['theBookThief', 'theHelp'],
  horror: ['it', 'theExorcist'],
  bookClubPicks: ['whereTheCrawdadsSing', 'theSevenHusbandsOfEvelynHugo'],
  romantasy: ['aCourtOfThornsAndRoses', 'fourthWing'],
  other: ['projectHailMary', 'theBookThief'],
} as const satisfies Record<FictionInterestId, readonly BookId[]>;

export const defaultBookIds: readonly BookId[] = [
  'yourMoneyOrYourLife',
  'theBodyKeepsTheScore',
  'atomicHabits',
];

// Round-robin across the groups (first of each, then second of each, ...), dropping repeats, so
// adjacent picks come from different answers instead of exhausting the first one.
export function interleaveBookIds(groups: readonly (readonly BookId[])[]): BookId[] {
  const result: BookId[] = [];
  const maxGroupLength = Math.max(0, ...groups.map(group => group.length));

  for (let index = 0; index < maxGroupLength; index++) {
    groups.forEach(group => {
      const bookId = group[index];

      if (bookId && !result.includes(bookId)) {
        result.push(bookId);
      }
    });
  }

  return result;
}
